import { clsx } from 'clsx';

type StatusVariant = 'success' | 'warning' | 'error' | 'info' | 'neutral';

interface StatusBadgeProps {
  status: string;
  variant?: StatusVariant;
  size?: 'sm' | 'md';
  dot?: boolean;
}

const STATUS_VARIANT_MAP: Record<string, StatusVariant> = {
  success: 'success', healthy: 'success', ok: 'success', active: 'success',
  completed: 'success', resolved: 'success', green: 'success',
  warning: 'warning', warn: 'warning', degraded: 'warning', yellow: 'warning',
  experimental: 'warning', pending: 'warning',
  error: 'error', failure: 'error', failed: 'error', alert: 'error',
  down: 'error', red: 'error', critical: 'error', rolled_back: 'error',
  in_progress: 'info', running: 'info', queued: 'info',
  stopped: 'neutral', partial: 'warning', empty: 'neutral',
  unknown: 'neutral', no_data: 'neutral', deprecated: 'neutral',
  grey: 'neutral', gray: 'neutral',
};

const variantStyles: Record<StatusVariant, string> = {
  success: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
  warning: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
  error: 'bg-rose-50 text-rose-700 dark:bg-rose-950/30 dark:text-rose-300',
  info: 'bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300',
  neutral: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const dotStyles: Record<StatusVariant, string> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-rose-500',
  info: 'bg-sky-500',
  neutral: 'bg-slate-400',
};

function resolveVariant(status: string, variant?: StatusVariant): StatusVariant {
  if (variant) return variant;
  const normalized = status.toLowerCase().replace(/[\s-]/g, '_');
  return STATUS_VARIANT_MAP[normalized] ?? 'neutral';
}

export default function StatusBadge({ status, variant, size = 'sm', dot = true }: StatusBadgeProps) {
  const resolved = resolveVariant(status, variant);
  const label = status.replace(/_/g, ' ');

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-md font-medium capitalize',
        variantStyles[resolved],
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
      )}
    >
      {dot && (
        <span className={clsx('inline-block shrink-0 rounded-full', dotStyles[resolved], size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2')} />
      )}
      {label}
    </span>
  );
}
